import {
  GraphQLList,
  GraphQLNonNull,
  GraphQLSchema,
  GraphQLType,
} from 'graphql';
import * as jsoncParser from 'jsonc-parser';
import * as monacoEditor from 'monaco-editor';

import { assertUnreachable } from 'src/lib/assertUnreachable';
import { unwrapNonNullType } from 'src/lib/graphql/graphqlHelpers';

import { getCurrentDocsContext } from '../explorerDocs/getFieldToggle/getCurrentDocsContext';

import { GraphQLPath } from './operationPathHelpers';
import { JSONObject, JSONValue } from './types';

export function parseVariablesOrDefault(
  variablesString: string | null,
): JSONObject {
  try {
    const parsedVariablesValues: JSONValue = jsoncParser.parse(
      variablesString ?? '{}',
    );
    if (
      parsedVariablesValues &&
      typeof parsedVariablesValues === 'object' &&
      !Array.isArray(parsedVariablesValues)
    ) {
      return parsedVariablesValues;
    }
  } catch {
    // Ignore error
  }
  return {};
}

export function getVariablesUsedInOperation(
  schema: GraphQLSchema,
  editor: monacoEditor.editor.IStandaloneCodeEditor,
  position: monacoEditor.Position,
) {
  const model = editor.getModel();
  if (!model) return undefined;
  const currentDocsContext = getCurrentDocsContext(model, position, [], schema);

  return currentDocsContext.variables.map((variable) => variable.name);
}

export function getUsedVariablesString(
  schema: GraphQLSchema,
  editor: monacoEditor.editor.IStandaloneCodeEditor,
  variablesEditor: monacoEditor.editor.IStandaloneCodeEditor,
  position: monacoEditor.Position,
) {
  let variableString = variablesEditor.getValue();
  const includedVariables = getVariablesUsedInOperation(
    schema,
    editor,
    position,
  );
  if (!includedVariables) return variableString;

  try {
    const variables: JSONValue = jsoncParser.parse(variableString);

    Object.keys(variables ?? {})
      .filter((variableName) => !includedVariables.includes(variableName))
      .forEach((variableName) => {
        variableString = jsoncParser.applyEdits(
          variableString,
          jsoncParser.modify(variableString, [variableName], undefined, {
            formattingOptions: {
              insertSpaces: true,
              tabSize: 2,
            },
          }),
        );
      });
  } catch {
    // failed to remove keys from variables, send them all
  }
  return variableString;
}

export const docsPathToVariableJSONPath = (
  path: GraphQLPath,
  firstArgName: string,
  variablesEditorPosition: monacoEditor.Position | null | undefined,
  variableModel: monacoEditor.editor.ITextModel,
) => {
  // Get path to cursor
  let cursorPath: jsoncParser.JSONPath = [];
  try {
    const rootJsonNode =
      variablesEditorPosition &&
      jsoncParser.findNodeAtOffset(
        jsoncParser.parseTree(variableModel.getValue()),
        variableModel.getOffsetAt(variablesEditorPosition),
      );
    if (rootJsonNode) {
      cursorPath = jsoncParser.getNodePath(rootJsonNode);
    }
  } catch {
    // error parsing json, ignore cursor path
  }

  // Get path to docnode
  return path.flatMap((argNode, i) => {
    const argName = i === 0 ? firstArgName : argNode.name;
    const docNodePath: Array<string | number> = [argName];
    if (cursorPath[0] === argName) {
      cursorPath.shift();
    } else {
      // cursor path no longer matches, clear it out
      cursorPath = [];
    }
    const unwrappedType = unwrapNonNullType(argNode);
    if (path.length - 1 !== i && unwrappedType instanceof GraphQLList) {
      let currentType: GraphQLType = unwrappedType;
      while ('ofType' in currentType) {
        if (currentType instanceof GraphQLList) {
          const cursorIndex = cursorPath.shift();
          if (typeof cursorIndex === 'number') {
            docNodePath.push(cursorIndex);
          } else {
            // cursor path no longer matches, clear it out
            cursorPath = [];
            docNodePath.push(0);
          }
          currentType = currentType.ofType;
        } else if (currentType instanceof GraphQLNonNull) {
          currentType = currentType.ofType;
        } else {
          assertUnreachable(currentType);
        }
      }
    }
    return docNodePath;
  });
};
